import React, { useState, useEffect } from 'react';
import { Building2, Zap, FileText, Sparkles, Copy, Check, Bot, ChevronDown, ChevronUp } from 'lucide-react';
import { KeywordResearchProject, ServicePageKeywordMapping } from '../../types';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';

interface SeoMetadataWorkstationProps {
  project: KeywordResearchProject;
  masterPrimary: string;
  primaryVariations?: string[];
  masterSecondary: string;
  secondaryVariations?: string[];
  masterTertiary: string;
  tertiaryVariations?: string[];
  onUpdateProject: (updated: KeywordResearchProject) => void;
}

export const SeoMetadataWorkstation: React.FC<SeoMetadataWorkstationProps> = ({
  project,
  masterPrimary,
  primaryVariations = [],
  masterSecondary,
  secondaryVariations = [],
  masterTertiary,
  tertiaryVariations = [],
  onUpdateProject,
}) => {
  const businessName = project.businessProfile.businessName || project.clientName || 'Client Business';
  const location = (project.businessProfile.serviceAreas && project.businessProfile.serviceAreas[0]) || project.businessProfile.primaryLocation || project.businessProfile.country || '';
  const websiteUrl = project.businessProfile.websiteUrl || project.websiteUrl || 'https://www.example.com';

  const servicePages: ServicePageKeywordMapping[] = project.servicePageMappings || [];

  // State for AI Prompt Drawers & JSON Inputs per page
  const [openAiDrawerMap, setOpenAiDrawerMap] = useState<Record<string, boolean>>({ homepage: true });
  const [copiedPromptId, setCopiedPromptId] = useState<string | null>(null);
  const [aiJsonInputMap, setAiJsonInputMap] = useState<Record<string, string>>({});
  const [autoFillSuccessMap, setAutoFillSuccessMap] = useState<Record<string, string>>({});

  // Client-side fallback formula title generator
  const generateFormulaTitle = (primary: string, secondary: string): string => {
    const p = (primary || '').replace(/^Variation:\s*/i, '').trim();
    const s = (secondary || '').replace(/^Variation:\s*/i, '').trim();
    if (!p) return `Top Services in ${location} | ${businessName}`;

    if (s && s.toLowerCase() !== p.toLowerCase() && `${p} - ${s} | ${businessName}`.length <= 60) {
      return `${p} - ${s} | ${businessName}`;
    }
    if (`${p} in ${location} | ${businessName}`.length <= 60) {
      return `${p} in ${location} | ${businessName}`;
    }
    if (`${p} Services in ${location} | ${businessName}`.length <= 60) {
      return `${p} Services in ${location} | ${businessName}`;
    }
    return `${p} | ${businessName}`.substring(0, 65);
  };

  // Client-side fallback formula meta description generator
  const generateFormulaMeta = (primary: string, secondary: string, tertiary?: string): string => {
    const p = (primary || '').replace(/^Variation:\s*/i, '').trim();
    const s = (secondary || '').replace(/^Variation:\s*/i, '').trim();
    const t = (tertiary || '').replace(/^Variation:\s*/i, '').trim();

    const baseP = p ? p.charAt(0).toUpperCase() + p.slice(1) : 'expert solutions';
    const baseS = s ? s.toLowerCase() : 'quality service';
    const baseT = t ? ` and ${t.toLowerCase()}` : '';

    const meta1 = `Looking for ${baseP} in ${location}? We specialize in ${baseS}${baseT} with guaranteed reliability. Contact our expert team today for a free quote!`;
    if (meta1.length >= 140 && meta1.length <= 160) return meta1;
    if (meta1.length > 160) {
      return `Looking for ${baseP} in ${location}? We provide trusted ${baseS} with fast turnaround. Call our certified team today!`.substring(0, 160);
    }
    return `${meta1} Learn more about our full range of commercial services online now!`.substring(0, 160);
  };

  // Auto-persist default SEO metadata to project on mount if not present
  useEffect(() => {
    let modified = false;
    let updatedProject = { ...project };

    if (!project.homepageSeoTitle) {
      updatedProject.homepageSeoTitle = generateFormulaTitle(masterPrimary, masterSecondary);
      modified = true;
    }
    if (!project.homepageMetaDescription) {
      updatedProject.homepageMetaDescription = generateFormulaMeta(masterPrimary, masterSecondary, masterTertiary);
      modified = true;
    }

    const currentPages = project.servicePageMappings || [];
    const updatedPages = currentPages.map(sp => {
      let title = sp.seoTitle;
      let meta = sp.metaDescription;
      if (!title) {
        title = generateFormulaTitle(sp.primaryKeyword || '', sp.secondaryKeyword || '');
        modified = true;
      }
      if (!meta) {
        meta = generateFormulaMeta(sp.primaryKeyword || '', sp.secondaryKeyword || '', sp.tertiaryKeyword || '');
        modified = true;
      }
      return { ...sp, seoTitle: title, metaDescription: meta };
    });

    if (modified) {
      updatedProject.servicePageMappings = updatedPages;
      onUpdateProject(updatedProject);
    }
  }, []);

  // State handlers for Homepage Metadata
  const homepageTitle = project.homepageSeoTitle || generateFormulaTitle(masterPrimary, masterSecondary);
  const homepageMeta = project.homepageMetaDescription || generateFormulaMeta(masterPrimary, masterSecondary, masterTertiary);

  const handleUpdateHomepageMetadata = (title: string, meta: string) => {
    onUpdateProject({
      ...project,
      homepageSeoTitle: title,
      homepageMetaDescription: meta
    });
  };

  const handleUpdateServicePageMetadata = (pageId: string, title: string, meta: string) => {
    const updated = servicePages.map(sp => {
      if (sp.id === pageId) {
        return { ...sp, seoTitle: title, metaDescription: meta };
      }
      return sp;
    });
    onUpdateProject({
      ...project,
      servicePageMappings: updated
    });
  };

  // Toggle AI Prompt Drawer per page
  const toggleAiDrawer = (pageKey: string) => {
    setOpenAiDrawerMap(prev => ({ ...prev, [pageKey]: !prev[pageKey] }));
  };

  // Master Prompt Generator following exact user prompt template
  const buildMasterAiPrompt = (
    pageName: string,
    targetUrl: string,
    primary: string,
    pVars: string[],
    secondary: string,
    sVars: string[],
    tertiary: string,
    tVars: string[]
  ): string => {
    const pVarsText = pVars.length > 0 ? pVars.map(v => `"${v}"`).join('\n') : 'None';
    const sVarsText = sVars.length > 0 ? sVars.map(v => `"${v}"`).join('\n') : 'None';
    const tVarsText = tVars.length > 0 ? tVars.map(v => `"${v}"`).join('\n') : 'None';

    return `You are a senior SEO copywriter who writes metadata that sounds like it was written by a real business — NOT by an SEO tool.

Your job is to generate a Google SEO title tag and meta description from the page/business information and finalized keyword hierarchy provided below.

The MOST IMPORTANT objective is:

**Make the metadata sound natural, commercial, and human first. Then satisfy the keyword requirements without making the copy feel optimized.**

Do NOT produce robotic SEO phrasing.

Do NOT mechanically join keywords together.

Do NOT try to fit every keyword into the metadata.

Do NOT use awkward constructions simply because they contain exact-match keywords.

Think like a professional copywriter writing the actual Google snippet a customer would want to click.

---

# PAGE & BUSINESS CONTEXT

Business Name: ${businessName}
Target Location / Region: ${location}
Page Name: ${pageName}
Target URL Path: ${targetUrl}

---

# FINALIZED KEYWORD HIERARCHY

Primary Target Keyword:
"${primary || 'N/A'}"

Primary Keyword Variations:
${pVarsText}

Secondary Target Keyword:
"${secondary || 'N/A'}"

Secondary Keyword Variations:
${sVarsText}

Tertiary Target Keyword:
"${tertiary || 'N/A'}"

Tertiary Keyword Variations:
${tVarsText}

---

# CORE WRITING PHILOSOPHY

The keyword hierarchy tells you WHAT topics must be represented.

It does NOT tell you HOW to write the sentence.

Write the metadata exactly as a good human SEO copywriter would.

For example, if the business sells truck and auto parts in Trinidad, this is natural:

"Truck Parts Trinidad | Best Auto Parts in Trinidad"

This is NOT natural:

"Truck Parts Trinidad & Auto Parts Trinidad | United Trucking"

The second version looks like two keywords glued together.

The first version looks like an actual business title.

Therefore:

**NATURAL COMMERCIAL LANGUAGE ALWAYS TAKES PRIORITY OVER LITERAL KEYWORD CONCATENATION.**

However, mandatory keyword requirements must still be respected.

# 🎓 OFFICIAL COURSE LECTURE METADATA RULES & FORMULAS

## 1. SEO TITLE FORMULA & CONSTRAINTS (COURSE LECTURE SPECIFICATION):
* **Official Formula**: Primary Keyword + Secondary Keyword | Business Name (or Primary Keyword + Secondary Keyword)
* **Strict Character Count**: MUST be between **50 and 60 characters** long (Do NOT fall under 50 or exceed 60).
* **Hierarchy Rule**: The 🥇 Primary Keyword MUST come first in the title, followed naturally by the 🥈 Secondary Keyword.
* **No Keyword Stuffing**: Never repeat the exact same keyword phrase twice.
* Start with the exact Primary Keyword.
* Include the Secondary keyword/topic naturally.
* Communicate what the business offers like a real business title.

### Course Lecture Example:
Primary: "truck parts trinidad"
Secondary: "auto parts trinidad"
Good SEO Title (50-60 chars): "Truck Parts Trinidad | Best Auto Parts in Trinidad"

---

## 2. META DESCRIPTION FORMULA & CONSTRAINTS (COURSE LECTURE SPECIFICATION):
* **Official Formula**: Primary Keyword Variation Sentence + Secondary/Tertiary Keyword Benefit + Call To Action (CTA)
* **Strict Character Count**: MUST be between **140 and 160 characters** long (Do NOT fall under 140 or exceed 160).
* **Variations & Hyper-Relevance**: Incorporate variations of Primary, Secondary, and Tertiary keywords naturally to maximize Google relevance signals.
* **Mandatory Call-To-Action (CTA)**: MUST include a clear action step (e.g. "Contact us today for a free quote!", "Call our team now!", "Visit our site today!").

### Course Lecture Example:
"Looking for truck parts in Trinidad? We supply genuine and aftermarket truck and auto parts across Trinidad. Contact United Trucking Supplies today!"

---

### IMPORTANT

Do NOT write:

"Truck parts in Trinidad and auto parts in Trinidad and Tobago. Find quality parts. Call us today!"

This sounds like keyword stuffing.

Instead, write naturally:

"Looking for truck parts in Trinidad? We supply genuine and aftermarket truck and auto parts across Trinidad. Contact United Trucking Supplies today!"

The second version sounds like a real company speaking to a customer.

---

# NATURALNESS RULES

Before finalizing, ask:

**"Would a real business owner be comfortable putting this exact sentence on their Google listing?"**

If NO, rewrite it.

Avoid:

* Keyword chains
* Keyword repetition
* Unnecessary exact-match phrases
* "Best [keyword] [keyword]"
* "Buy [keyword] [keyword]"
* "Leading [keyword] [keyword]"
* Generic SEO filler
* "Discover our..."
* "Explore our..."
* "Your trusted source for..."
* "High-quality solutions for..."
* "Comprehensive range of..."
* Corporate-sounding filler
* Sentences created only to accommodate keywords

Use simple commercial language.

Prefer words such as:

* Looking for...
* We supply...
* We stock...
* Shop...
* Find...
* Get...
* Need...
* We provide...
* Contact us...
* Call us...
* Serving...
* For trucks, cars and commercial vehicles...

But only use claims supported by the supplied business information.

---

# KEYWORD INTEGRATION RULE

Think of keywords as a **topic hierarchy**, not as words that must be visibly glued together.

Primary keyword = the main search intent.

Secondary keyword = another important commercial offering/topic.

Your job is to make both topics clear while writing natural English.

For example:

Primary:
"truck parts trinidad"

Secondary:
"auto parts trinidad"

A natural title can be:

"Truck Parts Trinidad | Best Auto Parts in Trinidad"

A natural meta can be:

"Looking for truck parts in Trinidad? We supply genuine and aftermarket truck and auto parts across Trinidad. Contact United Trucking Supplies today!"

Do NOT force exact-match repetition when it damages readability.

---

# BUSINESS CLAIM RULE

Only use products, services, locations, brands, capabilities, or claims supported by the provided context.

Do NOT invent:

* Free delivery
* Nationwide shipping
* Same-day delivery
* Lowest prices
* Largest inventory
* Authorized dealer status
* Genuine/OEM status
* Years in business
* Guarantees
* Financing
* Online ordering

unless the supplied information explicitly supports them.

---

# CTA

End the meta description with a natural commercial CTA.

Examples:

"Contact us today."

"Call us today."

"Shop today."

"Get the parts you need today."

"Find the right parts today."

"Contact United Trucking Supplies today."

Choose whichever fits naturally.

Do not force a CTA that makes the sentence awkward.

---

# CHARACTER COUNT

Count the EXACT final strings.

Every character counts:

* Letters
* Spaces
* Numbers
* Punctuation
* Symbols
* Ampersands
* Vertical bars

TITLE:
Target 50–60 characters.

META DESCRIPTION:
Target exactly 152 characters when naturally achievable.

Acceptable range: 150–154.

Hard maximum: 160.

Do NOT damage natural copy merely to hit exactly 152 characters.

However, when several natural versions are possible, choose the one closest to 152 characters.

---

# FINAL QUALITY CONTROL

Before returning the answer, verify:

TITLE:

* Starts with exact Primary Keyword.
* Represents the Secondary topic naturally.
* Sounds human.
* Sounds commercially useful.
* Does not look like keyword stuffing.
* Is 50–60 characters.
* Does not exceed 60.

META:

* Represents the Primary topic naturally.
* Represents the Secondary topic naturally.
* Explains what the business offers.
* Sounds human.
* Is commercially persuasive.
* Contains a CTA.
* Does not make unsupported claims.
* Is 150–154 characters when possible.
* Never exceeds 160.

Most importantly:

**If the metadata looks like it was generated by an SEO tool, rewrite it.**

---

# OUTPUT FORMAT

Return ONLY valid JSON.

No markdown.
No explanation.
No commentary.

Use exactly:

{
  "seoTitle": "Your generated title",
  "metaDescription": "Your generated meta description",
  "titleCharCount": 0,
  "metaCharCount": 0
}

The character counts MUST exactly match the final strings.

Perform the character count yourself before returning the JSON.`;
  };

  // 1-Click Copy AI Prompt
  const handleCopyPrompt = (pageKey: string, promptText: string) => {
    navigator.clipboard.writeText(promptText);
    setCopiedPromptId(pageKey);
    setTimeout(() => setCopiedPromptId(null), 2500);
  };

  // 1-Click Auto-Fill Metadata from Pasted LLM JSON Output
  const handleApplyAiJson = (
    pageKey: string,
    onApply: (title: string, meta: string) => void
  ) => {
    const rawInput = aiJsonInputMap[pageKey] || '';
    if (!rawInput.trim()) {
      alert('⚠️ Please paste the AI JSON output into the text box before clicking Auto-Fill.');
      return;
    }

    try {
      const cleaned = rawInput.replace(/```json/gi, '').replace(/```/g, '').trim();
      const parsed = JSON.parse(cleaned);

      if (parsed.seoTitle && parsed.metaDescription) {
        onApply(parsed.seoTitle.trim(), parsed.metaDescription.trim());
        setAutoFillSuccessMap(prev => ({ ...prev, [pageKey]: `✓ Applied! Title (${parsed.seoTitle.length} chars), Meta (${parsed.metaDescription.length} chars)` }));
        setTimeout(() => setAutoFillSuccessMap(prev => ({ ...prev, [pageKey]: '' })), 4000);
      } else {
        alert('⚠️ Invalid JSON structure. JSON must contain "seoTitle" and "metaDescription" properties.');
      }
    } catch (err) {
      alert('⚠️ Unable to parse JSON. Please make sure you copied the full JSON response from ChatGPT/Gemini.');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      
      {/* METHODOLOGY BANNER */}
      <div style={{ background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.9))', border: '1px solid var(--accent-blue)', borderRadius: 'var(--radius-md)', padding: '16px' }}>
        <div style={{ fontSize: '15px', fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <FileText size={18} color="var(--accent-blue)" />
          <span>PAGE-BY-PAGE SEO METADATA WORKSTATION (TITLES & META DESCRIPTIONS)</span>
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.5' }}>
          Instructor Methodology (Lecture 11): Generate and fine-tune SEO Title Tags (50–60 chars) and Meta Descriptions (140–160 chars) for every page on the site (Homepage + Service Pages). Use the integrated <strong>AI Prompt Generator & Auto-Fill System</strong> to copy pre-filled keyword prompts and paste structured LLM JSON outputs directly into the fields.
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px', marginTop: '12px' }}>
          <div style={{ background: 'rgba(234, 179, 8, 0.1)', border: '1px solid rgba(234, 179, 8, 0.3)', padding: '10px', borderRadius: 'var(--radius-sm)', fontSize: '11px', color: '#e2e8f0' }}>
            <div style={{ fontWeight: 800, color: '#facc15', marginBottom: '2px' }}>📌 SEO Title Tag Formula</div>
            <code>[1 Primary Keyword] + [1 Secondary Keyword] \| [Brand/Location]</code>
            <div style={{ color: '#94a3b8', fontSize: '10px', marginTop: '4px' }}>Strict Target: <strong>50–60 Characters</strong> (fits Google 580px width)</div>
          </div>
          <div style={{ background: 'rgba(52, 211, 153, 0.1)', border: '1px solid rgba(52, 211, 153, 0.3)', padding: '10px', borderRadius: 'var(--radius-sm)', fontSize: '11px', color: '#e2e8f0' }}>
            <div style={{ fontWeight: 800, color: '#34d399', marginBottom: '2px' }}>📌 Meta Description Formula</div>
            <code>[Primary Variation] + [Secondary Variation] + [Call to Action]</code>
            <div style={{ color: '#94a3b8', fontSize: '10px', marginTop: '4px' }}>Strict Target: <strong>140–160 Characters</strong> (prevents Google truncation)</div>
          </div>
        </div>
      </div>

      {/* 1. HOMEPAGE METADATA CARD */}
      {(() => {
        const pageKey = 'homepage';
        const isDrawerOpen = !!openAiDrawerMap[pageKey];
        const promptText = buildMasterAiPrompt(
          'Homepage',
          '/',
          masterPrimary,
          primaryVariations,
          masterSecondary,
          secondaryVariations,
          masterTertiary,
          tertiaryVariations
        );

        return (
          <div style={{ background: '#020617', border: '1px solid #facc15', borderRadius: 'var(--radius-md)', padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Badge variant="complete">Homepage (/)</Badge>
                <span style={{ fontSize: '16px', fontWeight: 800, color: '#ffffff' }}>Homepage Meta Strategy</span>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <Button
                  variant="secondary"
                  size="sm"
                  style={{ color: '#c084fc', borderColor: '#a855f7', background: 'rgba(168, 85, 247, 0.12)', fontSize: '11px', fontWeight: 700 }}
                  onClick={() => toggleAiDrawer(pageKey)}
                >
                  <Sparkles size={13} style={{ marginRight: '4px' }} />
                  AI Prompt & Auto-Fill {isDrawerOpen ? <ChevronUp size={12} style={{ marginLeft: '4px' }} /> : <ChevronDown size={12} style={{ marginLeft: '4px' }} />}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  style={{ color: '#facc15', borderColor: '#facc15', background: 'rgba(234, 179, 8, 0.1)', fontSize: '11px' }}
                  onClick={() => {
                    const genT = generateFormulaTitle(masterPrimary, masterSecondary);
                    const genM = generateFormulaMeta(masterPrimary, masterSecondary, masterTertiary);
                    handleUpdateHomepageMetadata(genT, genM);
                  }}
                >
                  <Zap size={13} style={{ marginRight: '4px' }} /> Apply Quick Formula
                </Button>
              </div>
            </div>

            {/* STEP 10 FINALIZED KEYWORD HIERARCHY & ATTACHED VARIATIONS DISPLAY */}
            <div style={{ background: 'rgba(15, 23, 42, 0.85)', border: '1px solid rgba(255,255,255,0.1)', padding: '14px', borderRadius: 'var(--radius-sm)', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ fontSize: '11px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                📌 STEP 10 FINALIZED HOMEPAGE KEYWORD TARGETS & ATTACHED VARIATIONS:
              </div>

              {/* 🥇 PRIMARY TARGET & ATTACHED VARIATIONS */}
              <div style={{ background: 'rgba(234, 179, 8, 0.08)', border: '1px solid rgba(234, 179, 8, 0.3)', borderRadius: '6px', padding: '10px 12px' }}>
                <div style={{ fontSize: '12px', fontWeight: 800, color: '#facc15', marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>🥇 PRIMARY TARGET: "{masterPrimary}"</span>
                  <span style={{ fontSize: '10px', background: 'rgba(234, 179, 8, 0.2)', padding: '2px 8px', borderRadius: '4px' }}>{primaryVariations.length} Variations</span>
                </div>
                {primaryVariations.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {primaryVariations.map((v, idx) => (
                      <span key={`p-var-${idx}`} style={{ background: 'rgba(234, 179, 8, 0.2)', border: '1px solid rgba(234, 179, 8, 0.4)', color: '#ffffff', borderRadius: '4px', padding: '3px 8px', fontSize: '11px', fontWeight: 600 }}>
                        {v}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' }}>No variations attached to Primary target in Step 10.</div>
                )}
              </div>

              {/* 🥈 SECONDARY TARGET & ATTACHED VARIATIONS */}
              <div style={{ background: 'rgba(16, 185, 129, 0.08)', border: '1px solid rgba(16, 185, 129, 0.3)', borderRadius: '6px', padding: '10px 12px' }}>
                <div style={{ fontSize: '12px', fontWeight: 800, color: '#34d399', marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>🥈 SECONDARY TARGET: "{masterSecondary}"</span>
                  <span style={{ fontSize: '10px', background: 'rgba(16, 185, 129, 0.2)', padding: '2px 8px', borderRadius: '4px' }}>{secondaryVariations.length} Variations</span>
                </div>
                {secondaryVariations.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {secondaryVariations.map((v, idx) => (
                      <span key={`s-var-${idx}`} style={{ background: 'rgba(16, 185, 129, 0.2)', border: '1px solid rgba(16, 185, 129, 0.4)', color: '#ffffff', borderRadius: '4px', padding: '3px 8px', fontSize: '11px', fontWeight: 600 }}>
                        {v}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' }}>No variations attached to Secondary target in Step 10.</div>
                )}
              </div>

              {/* 🥉 TERTIARY TARGET & ATTACHED VARIATIONS */}
              <div style={{ background: 'rgba(56, 189, 248, 0.08)', border: '1px solid rgba(56, 189, 248, 0.3)', borderRadius: '6px', padding: '10px 12px' }}>
                <div style={{ fontSize: '12px', fontWeight: 800, color: '#38bdf8', marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>🥉 TERTIARY TARGET: "{masterTertiary}"</span>
                  <span style={{ fontSize: '10px', background: 'rgba(56, 189, 248, 0.2)', padding: '2px 8px', borderRadius: '4px' }}>{tertiaryVariations.length} Variations</span>
                </div>
                {tertiaryVariations.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {tertiaryVariations.map((v, idx) => (
                      <span key={`t-var-${idx}`} style={{ background: 'rgba(56, 189, 248, 0.2)', border: '1px solid rgba(56, 189, 248, 0.4)', color: '#ffffff', borderRadius: '4px', padding: '3px 8px', fontSize: '11px', fontWeight: 600 }}>
                        {v}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' }}>No variations attached to Tertiary target in Step 10.</div>
                )}
              </div>
            </div>

            {/* AI PROMPT & AUTO-FILL SYSTEM DRAWER */}
            {isDrawerOpen && (
              <div style={{ background: '#090d16', border: '1px solid rgba(168, 85, 247, 0.4)', borderRadius: 'var(--radius-md)', padding: '16px', marginBottom: '18px' }}>
                <div style={{ fontSize: '13px', fontWeight: 800, color: '#c084fc', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Bot size={16} />
                  <span>HOMEPAGE AI METADATA PROMPT GENERATOR & 1-CLICK AUTO-FILL</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' }}>
                  {/* LEFT: Pre-Filled Prompt */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#e2e8f0' }}>Step 1: Copy Pre-filled Prompt (With Step 10 Hierarchy & Strict Formulas)</span>
                      <Button
                        variant="secondary"
                        size="sm"
                        style={{ fontSize: '11px', background: copiedPromptId === pageKey ? '#166534' : 'rgba(168, 85, 247, 0.2)', borderColor: '#a855f7', color: '#fff' }}
                        onClick={() => handleCopyPrompt(pageKey, promptText)}
                      >
                        {copiedPromptId === pageKey ? <Check size={12} style={{ marginRight: '4px' }} /> : <Copy size={12} style={{ marginRight: '4px' }} />}
                        {copiedPromptId === pageKey ? 'Copied Prompt!' : 'Copy AI Prompt'}
                      </Button>
                    </div>
                    <textarea
                      readOnly
                      rows={8}
                      className="input-field"
                      style={{ fontSize: '11px', fontFamily: 'monospace', color: '#94a3b8', background: '#020617', width: '100%', lineHeight: '1.4' }}
                      value={promptText}
                    />
                  </div>

                  {/* RIGHT: Paste Output & Auto-Fill */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#34d399' }}>Step 2: Paste ChatGPT / Gemini Output JSON & Auto-Fill</span>
                      {autoFillSuccessMap[pageKey] && (
                        <span style={{ fontSize: '10px', color: '#34d399', fontWeight: 700 }}>{autoFillSuccessMap[pageKey]}</span>
                      )}
                    </div>
                    <textarea
                      rows={6}
                      className="input-field"
                      placeholder={`Paste raw LLM JSON response here...\n\nExample:\n{\n  "seoTitle": "${masterPrimary} + ${masterSecondary} | ${businessName}",\n  "metaDescription": "Looking for ${masterPrimary} in ${location}? We supply certified ${secondaryVariations[0] || masterSecondary} with fast turnaround. Call our team today!"\n}`}
                      style={{ fontSize: '11px', fontFamily: 'monospace', color: '#ffffff', width: '100%', marginBottom: '8px' }}
                      value={aiJsonInputMap[pageKey] || ''}
                      onChange={(e) => setAiJsonInputMap(prev => ({ ...prev, [pageKey]: e.target.value }))}
                    />
                    <Button
                      variant="primary"
                      size="sm"
                      style={{ width: '100%', fontSize: '12px', background: 'linear-gradient(135deg, #059669, #10b981)' }}
                      onClick={() => handleApplyAiJson(pageKey, handleUpdateHomepageMetadata)}
                    >
                      <Zap size={14} style={{ marginRight: '6px' }} />
                      Auto-Fill Homepage Metadata Fields
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* LIVE GOOGLE SERP PREVIEW */}
            <div style={{ background: '#202124', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
              <div style={{ fontSize: '10px', color: '#9aa0a6', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px', fontWeight: 700 }}>
                🔍 LIVE GOOGLE SERP SNIPPET PREVIEW (DESKTOP)
              </div>
              <div style={{ fontSize: '12px', color: '#dadce0', marginBottom: '2px' }}>
                {websiteUrl}
              </div>
              <div style={{ fontSize: '18px', color: '#8ab4f8', fontWeight: 400, lineHeight: 1.3, marginBottom: '4px' }}>
                {homepageTitle.length > 60 ? `${homepageTitle.substring(0, 60)}...` : homepageTitle}
              </div>
              <div style={{ fontSize: '13px', color: '#bdc1c6', lineHeight: '1.4' }}>
                {homepageMeta.length > 160 ? `${homepageMeta.substring(0, 160)}...` : homepageMeta}
              </div>
            </div>

            {/* MANUAL EDIT INPUT FIELDS */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '14px' }}>
              {/* Title */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: '#facc15' }}>SEO Title Tag (50-60 Chars):</label>
                  <Badge variant={homepageTitle.length >= 50 && homepageTitle.length <= 60 ? 'complete' : homepageTitle.length > 60 ? 'lecture' : 'tool'}>
                    {homepageTitle.length} / 60 Chars {homepageTitle.length >= 50 && homepageTitle.length <= 60 ? '✓ Ideal' : homepageTitle.length > 60 ? '⚠️ Will Truncate' : '⚠️ Short'}
                  </Badge>
                </div>
                <input
                  type="text"
                  className="input-field"
                  style={{ padding: '8px 10px', fontSize: '12px', fontWeight: 700, width: '100%', color: '#ffffff' }}
                  value={homepageTitle}
                  onChange={(e) => handleUpdateHomepageMetadata(e.target.value, homepageMeta)}
                />
              </div>

              {/* Meta Description */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: '#34d399' }}>Meta Description Tag (140-160 Chars):</label>
                  <Badge variant={homepageMeta.length >= 140 && homepageMeta.length <= 160 ? 'complete' : homepageMeta.length > 160 ? 'lecture' : 'tool'}>
                    {homepageMeta.length} / 160 Chars {homepageMeta.length >= 140 && homepageMeta.length <= 160 ? '✓ Ideal' : homepageMeta.length > 160 ? '⚠️ Will Truncate' : '⚠️ Short'}
                  </Badge>
                </div>
                <textarea
                  className="input-field"
                  rows={3}
                  style={{ padding: '8px 10px', fontSize: '12px', width: '100%', color: '#ffffff', resize: 'vertical' }}
                  value={homepageMeta}
                  onChange={(e) => handleUpdateHomepageMetadata(homepageTitle, e.target.value)}
                />
              </div>
            </div>
          </div>
        );
      })()}

      {/* 2. SERVICE PAGES METADATA CARDS */}
      <div style={{ fontSize: '15px', fontWeight: 800, color: '#ffffff', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Building2 size={18} color="var(--accent-blue)" />
        <span>SERVICE PAGES METADATA ({servicePages.length} PAGES)</span>
      </div>

      {servicePages.map((sp, idx) => {
        const pageKey = sp.id;
        const isDrawerOpen = !!openAiDrawerMap[pageKey];

        const primaryVars = [
          ...(sp.primaryKeyword2 ? [sp.primaryKeyword2] : []),
          ...(sp.supportingKeywords || [])
        ];

        const secondaryVars = [
          ...(sp.secondaryKeyword2 ? [sp.secondaryKeyword2] : [])
        ];

        const tertiaryVars = [
          ...(sp.tertiaryKeyword2 ? [sp.tertiaryKeyword2] : [])
        ];

        const promptText = buildMasterAiPrompt(
          sp.pageName || `Service Page #${idx + 1}`,
          sp.targetUrl,
          sp.primaryKeyword,
          primaryVars,
          sp.secondaryKeyword,
          secondaryVars,
          sp.tertiaryKeyword,
          tertiaryVars
        );

        const title = sp.seoTitle || generateFormulaTitle(sp.primaryKeyword, sp.secondaryKeyword);
        const meta = sp.metaDescription || generateFormulaMeta(sp.primaryKeyword, sp.secondaryKeyword, sp.tertiaryKeyword);

        const titleOk = title.length >= 50 && title.length <= 60;
        const metaOk = meta.length >= 140 && meta.length <= 160;

        return (
          <div key={sp.id} style={{ background: '#020617', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Badge variant="tool">Service #{idx + 1}</Badge>
                <span style={{ fontSize: '16px', fontWeight: 800, color: '#ffffff' }}>{sp.pageName || `Service Page #${idx + 1}`}</span>
                <span style={{ fontSize: '11px', fontFamily: 'monospace', color: '#38bdf8' }}>{sp.targetUrl}</span>
              </div>

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <Button
                  variant="secondary"
                  size="sm"
                  style={{ color: '#c084fc', borderColor: '#a855f7', background: 'rgba(168, 85, 247, 0.12)', fontSize: '11px', fontWeight: 700 }}
                  onClick={() => toggleAiDrawer(pageKey)}
                >
                  <Sparkles size={13} style={{ marginRight: '4px' }} />
                  AI Prompt & Auto-Fill {isDrawerOpen ? <ChevronUp size={12} style={{ marginLeft: '4px' }} /> : <ChevronDown size={12} style={{ marginLeft: '4px' }} />}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  style={{ color: '#38bdf8', borderColor: '#38bdf8', background: 'rgba(56, 189, 248, 0.1)', fontSize: '11px' }}
                  onClick={() => {
                    const genT = generateFormulaTitle(sp.primaryKeyword, sp.secondaryKeyword);
                    const genM = generateFormulaMeta(sp.primaryKeyword, sp.secondaryKeyword, sp.tertiaryKeyword);
                    handleUpdateServicePageMetadata(sp.id, genT, genM);
                  }}
                >
                  <Zap size={13} style={{ marginRight: '4px' }} /> Apply Quick Formula
                </Button>
              </div>
            </div>

            {/* STEP 11 FINALIZED SERVICE KEYWORD TARGETS & ATTACHED VARIATIONS DISPLAY */}
            <div style={{ background: 'rgba(15, 23, 42, 0.85)', border: '1px solid rgba(255,255,255,0.1)', padding: '14px', borderRadius: 'var(--radius-sm)', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ fontSize: '11px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                📌 STEP 11 FINALIZED SERVICE KEYWORD TARGETS & ATTACHED VARIATIONS:
              </div>

              {/* 🥇 PRIMARY TARGET & ATTACHED VARIATIONS */}
              <div style={{ background: 'rgba(234, 179, 8, 0.08)', border: '1px solid rgba(234, 179, 8, 0.3)', borderRadius: '6px', padding: '10px 12px' }}>
                <div style={{ fontSize: '12px', fontWeight: 800, color: '#facc15', marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>🥇 PRIMARY TARGET: "{sp.primaryKeyword || 'Not Set'}"</span>
                  <span style={{ fontSize: '10px', background: 'rgba(234, 179, 8, 0.2)', padding: '2px 8px', borderRadius: '4px' }}>{primaryVars.length} Variations</span>
                </div>
                {primaryVars.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {primaryVars.map((v, vIdx) => (
                      <span key={`sp-p-var-${idx}-${vIdx}`} style={{ background: 'rgba(234, 179, 8, 0.2)', border: '1px solid rgba(234, 179, 8, 0.4)', color: '#ffffff', borderRadius: '4px', padding: '3px 8px', fontSize: '11px', fontWeight: 600 }}>
                        {v}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' }}>No variations attached to Primary target in Step 11.</div>
                )}
              </div>

              {/* 🥈 SECONDARY TARGET & ATTACHED VARIATIONS */}
              <div style={{ background: 'rgba(16, 185, 129, 0.08)', border: '1px solid rgba(16, 185, 129, 0.3)', borderRadius: '6px', padding: '10px 12px' }}>
                <div style={{ fontSize: '12px', fontWeight: 800, color: '#34d399', marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>🥈 SECONDARY TARGET: "{sp.secondaryKeyword || 'Not Set'}"</span>
                  <span style={{ fontSize: '10px', background: 'rgba(16, 185, 129, 0.2)', padding: '2px 8px', borderRadius: '4px' }}>{secondaryVars.length} Variations</span>
                </div>
                {secondaryVars.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {secondaryVars.map((v, vIdx) => (
                      <span key={`sp-s-var-${idx}-${vIdx}`} style={{ background: 'rgba(16, 185, 129, 0.2)', border: '1px solid rgba(16, 185, 129, 0.4)', color: '#ffffff', borderRadius: '4px', padding: '3px 8px', fontSize: '11px', fontWeight: 600 }}>
                        {v}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' }}>No variations attached to Secondary target in Step 11.</div>
                )}
              </div>

              {/* 🥉 TERTIARY TARGET & ATTACHED VARIATIONS */}
              <div style={{ background: 'rgba(56, 189, 248, 0.08)', border: '1px solid rgba(56, 189, 248, 0.3)', borderRadius: '6px', padding: '10px 12px' }}>
                <div style={{ fontSize: '12px', fontWeight: 800, color: '#38bdf8', marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>🥉 TERTIARY TARGET: "{sp.tertiaryKeyword || 'Not Set'}"</span>
                  <span style={{ fontSize: '10px', background: 'rgba(56, 189, 248, 0.2)', padding: '2px 8px', borderRadius: '4px' }}>{tertiaryVars.length} Variations</span>
                </div>
                {tertiaryVars.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {tertiaryVars.map((v, vIdx) => (
                      <span key={`sp-t-var-${idx}-${vIdx}`} style={{ background: 'rgba(56, 189, 248, 0.2)', border: '1px solid rgba(56, 189, 248, 0.4)', color: '#ffffff', borderRadius: '4px', padding: '3px 8px', fontSize: '11px', fontWeight: 600 }}>
                        {v}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' }}>No variations attached to Tertiary target in Step 11.</div>
                )}
              </div>
            </div>

            {/* AI PROMPT & AUTO-FILL SYSTEM DRAWER */}
            {isDrawerOpen && (
              <div style={{ background: '#090d16', border: '1px solid rgba(168, 85, 247, 0.4)', borderRadius: 'var(--radius-md)', padding: '16px', marginBottom: '18px' }}>
                <div style={{ fontSize: '13px', fontWeight: 800, color: '#c084fc', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Bot size={16} />
                  <span>SERVICE #{idx + 1} AI METADATA PROMPT GENERATOR & 1-CLICK AUTO-FILL</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' }}>
                  {/* LEFT: Pre-Filled Prompt */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#e2e8f0' }}>Step 1: Copy Pre-filled Prompt (With Service Variations & Strict Formulas)</span>
                      <Button
                        variant="secondary"
                        size="sm"
                        style={{ fontSize: '11px', background: copiedPromptId === pageKey ? '#166534' : 'rgba(168, 85, 247, 0.2)', borderColor: '#a855f7', color: '#fff' }}
                        onClick={() => handleCopyPrompt(pageKey, promptText)}
                      >
                        {copiedPromptId === pageKey ? <Check size={12} style={{ marginRight: '4px' }} /> : <Copy size={12} style={{ marginRight: '4px' }} />}
                        {copiedPromptId === pageKey ? 'Copied Prompt!' : 'Copy AI Prompt'}
                      </Button>
                    </div>
                    <textarea
                      readOnly
                      rows={8}
                      className="input-field"
                      style={{ fontSize: '11px', fontFamily: 'monospace', color: '#94a3b8', background: '#020617', width: '100%', lineHeight: '1.4' }}
                      value={promptText}
                    />
                  </div>

                  {/* RIGHT: Paste Output & Auto-Fill */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#34d399' }}>Step 2: Paste ChatGPT / Gemini Output JSON & Auto-Fill</span>
                      {autoFillSuccessMap[pageKey] && (
                        <span style={{ fontSize: '10px', color: '#34d399', fontWeight: 700 }}>{autoFillSuccessMap[pageKey]}</span>
                      )}
                    </div>
                    <textarea
                      rows={6}
                      className="input-field"
                      placeholder={`Paste raw LLM JSON response here...\n\nExample:\n{\n  "seoTitle": "${sp.primaryKeyword || 'Service'} + ${sp.secondaryKeyword || 'Spares'} | ${businessName}",\n  "metaDescription": "Looking for ${sp.primaryKeyword || 'service'} in ${location}? We supply certified parts with fast turnaround. Contact us today!"\n}`}
                      style={{ fontSize: '11px', fontFamily: 'monospace', color: '#ffffff', width: '100%', marginBottom: '8px' }}
                      value={aiJsonInputMap[pageKey] || ''}
                      onChange={(e) => setAiJsonInputMap(prev => ({ ...prev, [pageKey]: e.target.value }))}
                    />
                    <Button
                      variant="primary"
                      size="sm"
                      style={{ width: '100%', fontSize: '12px', background: 'linear-gradient(135deg, #059669, #10b981)' }}
                      onClick={() => handleApplyAiJson(pageKey, (t, m) => handleUpdateServicePageMetadata(sp.id, t, m))}
                    >
                      <Zap size={14} style={{ marginRight: '6px' }} />
                      Auto-Fill Service #{idx + 1} Metadata Fields
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* LIVE GOOGLE SERP PREVIEW */}
            <div style={{ background: '#202124', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
              <div style={{ fontSize: '10px', color: '#9aa0a6', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px', fontWeight: 700 }}>
                🔍 LIVE GOOGLE SERP SNIPPET PREVIEW (DESKTOP)
              </div>
              <div style={{ fontSize: '12px', color: '#dadce0', marginBottom: '2px' }}>
                {websiteUrl} <span style={{ color: '#bdc1c6' }}>› {sp.targetUrl.replace(/^\//, '')}</span>
              </div>
              <div style={{ fontSize: '18px', color: '#8ab4f8', fontWeight: 400, lineHeight: 1.3, marginBottom: '4px' }}>
                {title.length > 60 ? `${title.substring(0, 60)}...` : title}
              </div>
              <div style={{ fontSize: '13px', color: '#bdc1c6', lineHeight: '1.4' }}>
                {meta.length > 160 ? `${meta.substring(0, 160)}...` : meta}
              </div>
            </div>

            {/* MANUAL EDIT INPUT FIELDS */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '14px' }}>
              {/* Title */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: '#facc15' }}>SEO Title Tag (50-60 Chars):</label>
                  <Badge variant={titleOk ? 'complete' : title.length > 60 ? 'lecture' : 'tool'}>
                    {title.length} / 60 Chars {titleOk ? '✓ Ideal' : title.length > 60 ? '⚠️ Will Truncate' : '⚠️ Short'}
                  </Badge>
                </div>
                <input
                  type="text"
                  className="input-field"
                  style={{ padding: '8px 10px', fontSize: '12px', fontWeight: 700, width: '100%', color: '#ffffff' }}
                  value={title}
                  onChange={(e) => handleUpdateServicePageMetadata(sp.id, e.target.value, meta)}
                />
              </div>

              {/* Meta Description */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: '#34d399' }}>Meta Description Tag (140-160 Chars):</label>
                  <Badge variant={metaOk ? 'complete' : meta.length > 160 ? 'lecture' : 'tool'}>
                    {meta.length} / 160 Chars {metaOk ? '✓ Ideal' : meta.length > 160 ? '⚠️ Will Truncate' : '⚠️ Short'}
                  </Badge>
                </div>
                <textarea
                  className="input-field"
                  rows={3}
                  style={{ padding: '8px 10px', fontSize: '12px', width: '100%', color: '#ffffff', resize: 'vertical' }}
                  value={meta}
                  onChange={(e) => handleUpdateServicePageMetadata(sp.id, title, e.target.value)}
                />
              </div>
            </div>
          </div>
        );
      })}

      {/* 3. MASTER METADATA SUMMARY TABLE */}
      <div style={{ border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', overflow: 'hidden', marginTop: '10px' }}>
        <div style={{ padding: '10px 14px', background: '#020617', borderBottom: '1px solid var(--border-color)', fontSize: '12px', fontWeight: 800, color: 'var(--accent-blue)', display: 'flex', justifyContent: 'space-between' }}>
          <span>📊 MASTER SITE SEO METADATA ARCHITECTURE SUMMARY</span>
          <span>{1 + servicePages.length} TOTAL PAGES</span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#090d16', color: '#94a3b8', fontSize: '11px', textTransform: 'uppercase' }}>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Page Name</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>URL Path</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Final SEO Title Tag</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Final Meta Description Tag</th>
            </tr>
          </thead>
          <tbody>
            {/* Homepage Row */}
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(234, 179, 8, 0.08)' }}>
              <td style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 800, color: '#facc15' }}>Homepage</td>
              <td style={{ padding: '8px 12px', fontSize: '11px', color: '#38bdf8', fontFamily: 'monospace' }}>/</td>
              <td style={{ padding: '8px 12px', fontSize: '12px', color: '#ffffff' }}>
                {homepageTitle} <Badge variant={homepageTitle.length >= 50 && homepageTitle.length <= 60 ? 'complete' : 'tool'}>{homepageTitle.length} chars</Badge>
              </td>
              <td style={{ padding: '8px 12px', fontSize: '11px', color: '#cbd5e1' }}>
                {homepageMeta} <Badge variant={homepageMeta.length >= 140 && homepageMeta.length <= 160 ? 'complete' : 'tool'}>{homepageMeta.length} chars</Badge>
              </td>
            </tr>
            {/* Service Page Rows */}
            {servicePages.map((sp, idx) => {
              const t = sp.seoTitle || generateFormulaTitle(sp.primaryKeyword, sp.secondaryKeyword);
              const m = sp.metaDescription || generateFormulaMeta(sp.primaryKeyword, sp.secondaryKeyword, sp.tertiaryKeyword);
              return (
                <tr key={`sum-${sp.id}`} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', background: idx % 2 === 0 ? 'rgba(15, 23, 42, 0.6)' : 'rgba(15, 23, 42, 0.3)' }}>
                  <td style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 800, color: '#ffffff' }}>{sp.pageName || `Service #${idx + 1}`}</td>
                  <td style={{ padding: '8px 12px', fontSize: '11px', color: '#38bdf8', fontFamily: 'monospace' }}>{sp.targetUrl}</td>
                  <td style={{ padding: '8px 12px', fontSize: '12px', color: '#ffffff' }}>
                    {t} <Badge variant={t.length >= 50 && t.length <= 60 ? 'complete' : 'tool'}>{t.length} chars</Badge>
                  </td>
                  <td style={{ padding: '8px 12px', fontSize: '11px', color: '#cbd5e1' }}>
                    {m} <Badge variant={m.length >= 140 && m.length <= 160 ? 'complete' : 'tool'}>{m.length} chars</Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

    </div>
  );
};
